import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Lock, CheckCircle2, Undo2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const LABEL = { provisional: 'Provisional', finalized: 'Finalized', paid: 'Paid' };
const TINT = { provisional: 'bg-amber-50 text-amber-700', finalized: 'bg-blue-50 text-blue-700', paid: 'bg-emerald-50 text-emerald-700' };

export const HltStatusBar = ({ data, onChanged }) => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === 'admin';
  const move = async status => {
    setBusy(true);
    try { onChanged((await api.post('/hot-transfer/status', { month: data.month, status, reason })).data); setReason(''); toast.success(`Marked ${LABEL[status]}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const last = (data.history || []).slice(-1)[0];
  return (
    <Card className="p-3 flex flex-wrap items-center gap-3" data-testid="hlt-status-bar">
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${TINT[data.status]}`} data-testid="hlt-status">
        {data.status !== 'provisional' && <Lock className="w-3 h-3" />}{LABEL[data.status]}</span>
      <span className="text-xs text-slate-500" data-testid="hlt-refreshed-at">
        {data.refreshed_at ? `Last read from CRM ${when(data.refreshed_at)} by ${data.refreshed_by}` : 'Not read from Zoho CRM yet — click Update from CRM'}
        {last && ` · ${last.by} moved it to ${LABEL[last.to]} ${when(last.at)}${last.reason ? ` (“${last.reason}”)` : ''}`}</span>
      <div className="ml-auto flex items-center gap-2">
        {data.status === 'provisional' && data.refreshed_at && <Button size="sm" disabled={busy} onClick={() => move('finalized')} data-testid="hlt-finalize-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Finalize</Button>}
        {data.status === 'finalized' && isAdmin && <Button size="sm" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => move('paid')} data-testid="hlt-paid-btn"><Wallet className="w-3.5 h-3.5 mr-1.5" />Mark paid</Button>}
        {data.status !== 'provisional' && isAdmin && (
          <>
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason to reopen" className="h-8 text-xs w-44" data-testid="hlt-reopen-reason" />
            <Button size="sm" variant="outline" disabled={busy || !reason.trim()} onClick={() => move('provisional')} data-testid="hlt-reopen-btn"><Undo2 className="w-3.5 h-3.5 mr-1.5" />Reopen</Button>
          </>
        )}
      </div>
    </Card>
  );
};
